import { z } from 'zod';
import { BaseOperationStepResult, TypeConfig, type StepConfig } from './operationSharedTypes.js';
import {
  DateTimeSchema,
  DurationLikeObject,
  ObjectIdSchema,
  ObjectIdString,
  SearchEntityQuery,
} from '../../common.js';
import { DataIQFieldConfig, DataIQLookupType } from '../configSchemas.js';
import {
  AnyCandidateProfile,
  DataIQProfilePromptType,
  DataIqUpdate,
} from '../../llmResponseSchemas.js';
import { LlmInputMetadata } from '../../../interfaces.js';
import { BullhornUpdateEntityResult } from '../../external/bullhornSchemas/index.js';

export const DataIqCustomFields = z.record(z.string(), z.string().or(z.number()).nullish());
export type DataIqCustomFields = z.infer<typeof DataIqCustomFields>;

export const DataIqOperationType = z.enum([
  'dataIqSingleCandidate',
  'dataIqSingleCandidateTest',
  'dataIqBatch',
  'dataIqTestBatch',
]);
export type DataIqOperationType = z.infer<typeof DataIqOperationType>;

export const DataIqOperationStepName = z.enum([
  'checkActivityForUpdate',
  'generateCandidateUpdate',
  'applyCandidateUpdate',
  'initializeCandidatesForBatch',
]);
export type DataIqOperationStepName = z.infer<typeof DataIqOperationStepName>;

export const dataIqOperationTypeConfigs = {
  dataIqSingleCandidate: {
    steps: ['checkActivityForUpdate', 'generateCandidateUpdate', 'applyCandidateUpdate'],
  },
  dataIqSingleCandidateTest: {
    steps: ['checkActivityForUpdate', 'generateCandidateUpdate'],
    isTestOperation: true,
  },
  dataIqBatch: {
    steps: ['initializeCandidatesForBatch'],
    hasChildOperations: true,
  },
  dataIqTestBatch: {
    steps: ['initializeCandidatesForBatch'],
    hasChildOperations: true,
    isTestOperation: true,
  },
} as const satisfies Record<DataIqOperationType, TypeConfig<DataIqOperationStepName>>;

//--------------------------------------------------------------------------------------------------------------------//
//region DataIQ Input Options

export const DataIqMappingContext = z
  .string()
  .trim()
  .optional()
  .describe(
    'Additional context for the mapping step, shared by every candidate in the batch. The LLM sees it for every enabled field, not just the one you mean, so name the target field in the text and only start the batch from candidates the text is true for. Does not bypass activity requirements or field write rules.'
  );

export const DataIqCheckForUpdateOptions = z.object({
  mappingContext: DataIqMappingContext,
  triggeringActivity: z
    .object({
      id: ObjectIdString,
      activityType: z.string(),
      timestamp: DateTimeSchema,
    })
    .optional(),
  testMode: z.boolean().optional(),
  // forceUpdate: true to skip relatedEntity date validation
  forceUpdate: z.boolean().optional(),
  refreshActivityData: z.boolean().optional(),
  lookupType: DataIQLookupType.optional(),
  lookBack: DurationLikeObject.optional(),
  promptTypeOverride: DataIQProfilePromptType.optional(),
  llmModelOverride: z.string().optional(),
});
export type DataIqCheckForUpdateOptions = z.infer<typeof DataIqCheckForUpdateOptions>;

export const DataIqGenerateUpdateOptions = z.object({
  mappingContext: DataIqMappingContext,
  parsedResponse: AnyCandidateProfile.nullish(),
  activityAuthorFirstNames: z.string().array().optional(),
});
export type DataIqGenerateUpdateOptions = z.infer<typeof DataIqGenerateUpdateOptions>;

export const DataIqUpdateCandidateOptions = z
  .object({
    ...DataIqUpdate.shape,
  })
  .partial();
export type DataIqUpdateCandidateOptions = z.infer<typeof DataIqUpdateCandidateOptions>;

export const DataIqInitializeCandidateOptions = z.object({
  candidateIds: ObjectIdSchema.array().default([]),
  sourceIds: z.string().array().default([]),
  searchQuery: SearchEntityQuery.nullish(),
  checkForUpdateOptions: DataIqCheckForUpdateOptions,
});
export type DataIqInitializeCandidateOptions = z.infer<typeof DataIqInitializeCandidateOptions>;

//endregion
//--------------------------------------------------------------------------------------------------------------------//
//region DataIQ Step Outputs

export const CheckForUpdateUpdateStepResult = z.object({
  ...BaseOperationStepResult.shape,
  responseId: ObjectIdSchema.nullish(),
  activityMetadata: LlmInputMetadata.nullish(),
  parsedResponse: AnyCandidateProfile.nullish(),
});
export type CheckForUpdateUpdateStepResult = z.infer<typeof CheckForUpdateUpdateStepResult>;

export const GenerateCandidateUpdateStepResult = z.object({
  ...BaseOperationStepResult.shape,
  mappingContext: DataIqMappingContext,
  responseId: ObjectIdSchema.nullish(),
  parsedResponse: z.record(z.string(), z.unknown()).nullish(),
  customFields: DataIqCustomFields.nullish(),
  currentValues: z.record(z.string(), z.unknown()).nullish(),
  detectedValues: z.record(z.string(), z.unknown()).nullish(),
  fieldUpdates: DataIqUpdate.shape.fieldUpdates.partial().nullish(),
  fieldConfigs: z.record(z.string(), DataIQFieldConfig.partial()).nullish(),
});
export type GenerateCandidateUpdateStepResult = z.infer<typeof GenerateCandidateUpdateStepResult>;

export const ApplyCandidateUpdateStepResult = z.looseObject({
  ...BaseOperationStepResult.shape,
  ...BullhornUpdateEntityResult.shape,
});
export type ApplyCandidateUpdateStepResult = z.infer<typeof ApplyCandidateUpdateStepResult>;

export const InitializeCandidateBatchStepResult = z.object({
  ...BaseOperationStepResult.shape,
  createdCandidates: ObjectIdSchema.array(),
  foundCandidates: ObjectIdSchema.array(),
  totalPopulation: z.number().nullish(),
});
export type InitializeCandidateBatchStepResult = z.infer<typeof InitializeCandidateBatchStepResult>;

//endregion
//--------------------------------------------------------------------------------------------------------------------//

export const dataIqOperationStepConfigs = {
  checkActivityForUpdate: {
    inputSchema: DataIqCheckForUpdateOptions,
    outputSchema: CheckForUpdateUpdateStepResult,
    singleCandidate: true,
  },
  generateCandidateUpdate: {
    inputSchema: DataIqGenerateUpdateOptions,
    outputSchema: GenerateCandidateUpdateStepResult,
    singleCandidate: true,
  },
  applyCandidateUpdate: {
    inputSchema: DataIqUpdateCandidateOptions,
    outputSchema: ApplyCandidateUpdateStepResult,
    singleCandidate: true,
  },
  initializeCandidatesForBatch: {
    inputSchema: DataIqInitializeCandidateOptions,
    outputSchema: InitializeCandidateBatchStepResult,
  },
} as const satisfies Record<DataIqOperationStepName, StepConfig>;
